// tegn-resolve.js – ren, testbar positions-kortlægning fra analysens segment-tekst til den
// levende (redigerede) segment-tekst. Ingen DOM, ingen Yjs: modulet køres af `node --test`
// (tests/test-tegn-resolve.mjs) og af tegn-panel.js i browseren.
//
// Baggrund: tegn-dataene (data/tegn.json) er en STATISK analysefil med offsets målt i
// segmentteksten som den så ud ved analysen, mens dokumentet redigeres live. Offsets skal
// derfor kortlægges mod den levende tekst, segment for segment, før de kan bruges.
//
// Status er IKKE kosmetik – den styrer hvad panelet må vise:
//   'uaendret'  intervallet ligger før enhver redigering; analysens offsets gælder uændret.
//   'flyttet'   intervallet er forskudt af en redigering andetsteds; teksten selv er intakt.
//   'redigeret' brugeren har rørt netop den passage – cellen SKAL da vise den LEVENDE tekst,
//               ikke analysens, ellers viser panelet noget der ikke længere står i dokumentet.
//   'vaek'      passagen findes ikke længere i den levende tekst; cellen må ikke være klikbar.

/**
 * Kortlæg intervallet [from, to) i `orig` til den levende tekst `live`.
 *
 * Metode: fælles præfiks `p` og fælles suffiks `s` beregnes med invarianten
 * p + s <= min(orig.length, live.length), så det ændrede område er
 * orig[p .. orig.length-s) -> live[p .. live.length-s).
 *   - Ligger intervallet helt før p: uændrede offsets ('uaendret').
 *   - Ligger det helt efter orig.length-s: forskudt med live.length-orig.length ('flyttet').
 *   - Ellers overlapper det redigeringen: endepunkter i det ændrede område klippes ind i
 *     live-tekstens ændrede område ('redigeret'); et tomt/ugyldigt resultat er 'vaek'.
 *
 * FALLBACK: giver kortlægningen ikke den oprindelige delstreng tilbage, søges delstrengen i
 * live-teksten på SAMME forekomst-indeks som i originalen; findes den, bruges den med status
 * 'flyttet'. Det redder tilfælde med to adskilte redigeringer i samme segment, hvor
 * præfiks/suffiks-kortlægningen degenererer (p og s klemmes ind mod enderne, og alt midt
 * imellem stemples 'redigeret' selv om passagen står urørt).
 *
 * @param {string} orig segmentteksten ved analysen
 * @param {string} live segmentteksten nu
 * @param {number} from start-offset i orig (inklusiv)
 * @param {number} to   slut-offset i orig (eksklusiv)
 * @returns {{from: number, to: number, status: 'uaendret'|'flyttet'|'redigeret'|'vaek'}}
 */
export function mapRange(orig, live, from, to) {
  if (typeof orig !== 'string' || typeof live !== 'string' ||
      !(from >= 0) || !(to > from) || to > orig.length) {
    return { from: 0, to: 0, status: 'vaek' };
  }
  if (orig === live) return { from, to, status: 'uaendret' };

  const minLen = Math.min(orig.length, live.length);
  let p = 0;
  while (p < minLen && orig.charCodeAt(p) === live.charCodeAt(p)) p++;
  let s = 0;
  while (s < minLen - p &&
         orig.charCodeAt(orig.length - 1 - s) === live.charCodeAt(live.length - 1 - s)) s++;
  const delta = live.length - orig.length;
  const origStableTail = orig.length - s;  // første stabile suffiks-position i orig
  const liveChangedEnd = live.length - s;  // det ændrede områdes slutning i live

  let res;
  if (to <= p) {
    res = { from, to, status: 'uaendret' };
  } else if (from >= origStableTail) {
    res = { from: from + delta, to: to + delta, status: 'flyttet' };
  } else {
    // Overlapper redigeringen: stabile endepunkter beholder deres kortlægning, endepunkter
    // inde i det ændrede område klippes ind i live-tekstens ændrede område.
    const nf = from <= p ? from : Math.min(from, liveChangedEnd);
    const nt = to >= origStableTail ? to + delta : Math.min(to, liveChangedEnd);
    if (nt > nf && nf >= 0 && nt <= live.length) res = { from: nf, to: nt, status: 'redigeret' };
    else res = { from: 0, to: 0, status: 'vaek' };
  }

  const needle = orig.slice(from, to);
  const mappedText = res.status === 'vaek' ? '' : live.slice(res.from, res.to);
  if (mappedText !== needle) {
    const occ = rangeOccIndex(orig, from, to);
    if (occ >= 0) {
      const hit = occIndexOf(live, needle, occ);
      if (hit >= 0) return { from: hit, to: hit + needle.length, status: 'flyttet' };
    }
  }
  return res;
}

/**
 * Indekset for den occ'te (0-baserede) forekomst af `needle` i `hay`, eller -1.
 * Overlappende forekomster tælles (søgningen fortsætter fra forrige indeks + 1), og
 * rangeOccIndex tæller på PRÆCIS samme måde – de to skal være enige for at fallbacket
 * i mapRange rammer den rigtige forekomst.
 */
export function occIndexOf(hay, needle, occ) {
  if (!needle || !(occ >= 0)) return -1;
  let idx = -1;
  for (let i = 0; i <= occ; i++) {
    idx = hay.indexOf(needle, idx + 1);
    if (idx < 0) return -1;
  }
  return idx;
}

/**
 * Forekomst-indekset (0-baseret) af intervallet [from, to) i `orig`: hvor mange forekomster
 * af orig.slice(from, to) står FØR from? Bruges af mapRange-fallbacket til at genfinde
 * "den n'te forekomst" i live-teksten. Returnerer -1 for et tomt interval.
 */
export function rangeOccIndex(orig, from, to) {
  const needle = orig.slice(from, to);
  if (!needle) return -1;
  let idx = -1;
  let n = 0;
  for (;;) {
    idx = orig.indexOf(needle, idx + 1);
    if (idx < 0 || idx > from) return -1; // defensivt; kan ikke ske når needle er skåret ved from
    if (idx === from) return n;
    n++;
  }
}
